import React, { useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";

// My Files
import firebase from "../../firebase";

export default function ChangePasswordDialog(props) {
    const [currentPassword, setCurrentPassword] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmedNewPassword, setConfirmedNewPassword] = useState("");
    const [showCurrentPassword, setShowCurrentPassword] = useState(false);
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmedNewPassword, setShowConfirmedNewPassword] =
        useState(false);

    const [inProgress, setInProgress] = useState(false);

    const [statusMessage, setStatusMessage] = useState("");

    const handleClose = () => {
        if (inProgress) {
            return;
        }

        setCurrentPassword("");
        setNewPassword("");
        setConfirmedNewPassword("");
        setShowCurrentPassword(false);
        setShowNewPassword(false);
        setShowConfirmedNewPassword(false);
        setStatusMessage("");

        props.onClose();
    };

    const handleChange = (event) => {
        setStatusMessage("");

        if (event.target.id === "currentpassword") {
            setCurrentPassword(event.target.value);
        } else if (event.target.id === "newpassword") {
            setNewPassword(event.target.value);
        } else if (event.target.id === "confirmednewpassword") {
            setConfirmedNewPassword(event.target.value);
        }
    };

    function handleOKPress(event) {
        event.preventDefault();

        if (
            !currentPassword ||
            !newPassword ||
            !confirmedNewPassword ||
            inProgress
        ) {
            return;
        }

        const auth = firebase.auth();
        const user = auth.currentUser;

        setInProgress(true);
        setStatusMessage("");

        if (user) {
            const credential = firebase.auth.EmailAuthProvider.credential(
                user.email,
                currentPassword,
            );

            user.reauthenticateWithCredential(credential)
                .then(() => {
                    if (newPassword === confirmedNewPassword) {
                        user.updatePassword(newPassword)
                            .then(() => {
                                setInProgress(false);

                                props.onSuccess();

                                setCurrentPassword("");
                                setNewPassword("");
                                setConfirmedNewPassword("");
                                setShowCurrentPassword(false);
                                setShowNewPassword(false);
                                setShowConfirmedNewPassword(false);
                                setStatusMessage("");
                            })
                            .catch((error) => {
                                setInProgress(false);
                                setStatusMessage(error.message);
                            });
                    } else {
                        setInProgress(false);
                        setStatusMessage("New passwords do not match");
                    }
                })
                .catch(() => {
                    setCurrentPassword("");

                    setStatusMessage("Current password is incorrect");
                    setInProgress(false);
                });
        } else {
            setInProgress(false);
            setStatusMessage("Your session has expired. Please sign in again.");
        }
    }

    const hasAllPasswords = Boolean(
        currentPassword && newPassword && confirmedNewPassword,
    );

    return (
        <Dialog
            onClose={handleClose}
            aria-labelledby="change-password-dialog-title"
            open={props.open}
            fullWidth
            maxWidth="xs"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                    },
                },
            }}
        >
            <Box
                sx={{
                    position: "relative",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "action.hover",
                    }}
                >
                    <LockOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="change-password-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        Update password
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5, lineHeight: 1.55 }}
                    >
                        Choose a new password for your account.
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close update password"
                    onClick={handleClose}
                    disabled={inProgress}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>

                {inProgress && (
                    <LinearProgress
                        aria-label="Updating password"
                        sx={{
                            position: "absolute",
                            left: 0,
                            right: 0,
                            bottom: 0,
                        }}
                    />
                )}
            </Box>

            <Box component="form" noValidate onSubmit={handleOKPress}>
                <DialogContent
                    dividers
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        backgroundColor: "background.default",
                    }}
                >
                    {statusMessage && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {statusMessage}
                        </Alert>
                    )}

                    <Box
                        sx={{
                            p: { xs: 2, sm: 2.5 },
                            borderRadius: 2.5,
                            border: "1px solid",
                            borderColor: "divider",
                            backgroundColor: "background.paper",
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{ mb: 1.5, fontWeight: 800 }}
                        >
                            Password details
                        </Typography>
                        <Box sx={{ display: "grid", gap: 2 }}>
                            <TextField
                                id="currentpassword"
                                label="Current password"
                                type={showCurrentPassword ? "text" : "password"}
                                autoComplete="current-password"
                                variant="outlined"
                                value={currentPassword}
                                onChange={handleChange}
                                autoFocus
                                required
                                fullWidth
                                disabled={inProgress}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={
                                                    showCurrentPassword
                                                        ? "Hide current password"
                                                        : "Show current password"
                                                }
                                                onClick={() =>
                                                    setShowCurrentPassword(
                                                        (current) => !current,
                                                    )
                                                }
                                                edge="end"
                                                disabled={inProgress}
                                            >
                                                {showCurrentPassword ? (
                                                    <VisibilityOffOutlinedIcon />
                                                ) : (
                                                    <VisibilityOutlinedIcon />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                            <TextField
                                id="newpassword"
                                label="New password"
                                type={showNewPassword ? "text" : "password"}
                                autoComplete="new-password"
                                variant="outlined"
                                value={newPassword}
                                onChange={handleChange}
                                required
                                fullWidth
                                disabled={inProgress}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={
                                                    showNewPassword
                                                        ? "Hide new password"
                                                        : "Show new password"
                                                }
                                                onClick={() =>
                                                    setShowNewPassword(
                                                        (current) => !current,
                                                    )
                                                }
                                                edge="end"
                                                disabled={inProgress}
                                            >
                                                {showNewPassword ? (
                                                    <VisibilityOffOutlinedIcon />
                                                ) : (
                                                    <VisibilityOutlinedIcon />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                            <TextField
                                id="confirmednewpassword"
                                label="Confirm new password"
                                type={
                                    showConfirmedNewPassword
                                        ? "text"
                                        : "password"
                                }
                                autoComplete="new-password"
                                variant="outlined"
                                value={confirmedNewPassword}
                                onChange={handleChange}
                                required
                                fullWidth
                                disabled={inProgress}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                aria-label={
                                                    showConfirmedNewPassword
                                                        ? "Hide confirmed new password"
                                                        : "Show confirmed new password"
                                                }
                                                onClick={() =>
                                                    setShowConfirmedNewPassword(
                                                        (current) => !current,
                                                    )
                                                }
                                                edge="end"
                                                disabled={inProgress}
                                            >
                                                {showConfirmedNewPassword ? (
                                                    <VisibilityOffOutlinedIcon />
                                                ) : (
                                                    <VisibilityOutlinedIcon />
                                                )}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: 2,
                                    },
                                }}
                            />
                        </Box>
                        <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", mt: 1.25 }}
                        >
                            Enter your current password to confirm this change.
                        </Typography>
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{ px: { xs: 2, sm: 3 }, py: 1.5, gap: 1 }}
                >
                    <Button
                        color="inherit"
                        onClick={handleClose}
                        disabled={inProgress}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disableElevation
                        disabled={!hasAllPasswords || inProgress}
                    >
                        {inProgress ? "Updating…" : "Update password"}
                    </Button>
                </DialogActions>
            </Box>
        </Dialog>
    );
}
